import { Injectable, inject } from '@angular/core';
import { Group, GroupsService } from '@era/security-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthorizationService, Permissions } from '@rocksoft/shared/authorization';
import { mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { GroupApiActions as MemberGroupGroupApiActions } from '../member-group/member-group.actions';
import { GroupApiActions, GroupListItemUIActions, GroupListUIActions, GroupResolverActions } from './group.actions';

@Injectable()
export class GroupEffects {
  private readonly groupService = inject(GroupsService);
  private readonly actions$ = inject(Actions);
  private readonly authorizationService = inject(AuthorizationService);

  groupLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GroupResolverActions.groupsRequested),
      mergeMap(() => {
        return safeMap(
          () => {
            if (!this.authorizationService.getIsAuthorized(Permissions.SecurityListGroups)) {
              throw Error('Tried to load groups while unauthorized.');
            }

            return this.groupService.getGroups();
          },
          (groups: Group[]) => GroupApiActions.groupsLoaded({ groups }),
          (error) => GroupApiActions.groupsLoadedError({ error })
        );
      })
    );
  });

  groupDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GroupListItemUIActions.groupDeleteRequested),
      mergeMap(({ group }) =>
        safeMap(
          this.groupService.removeGroup({ id: group.id }),
          () => [GroupApiActions.groupDeleted({ group }), MemberGroupGroupApiActions.groupDeleted({ group })],
          (error) => GroupApiActions.groupDeletedError({ group, error })
        ).pipe(mergeMap((result) => (Array.isArray(result) ? result : [result])))
      )
    );
  });

  groupAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GroupListUIActions.groupAddRequested),
      mergeMap(({ group }) =>
        safeMap(
          this.groupService.addGroup({ createGroupCommand: group }),
          (group: Group) => GroupApiActions.groupAdded({ group }),
          (error) => GroupApiActions.groupAddedError({ group, error })
        )
      )
    );
  });
}
